import PropTypes from 'prop-types';
import classNames from 'classnames';
import download from 'downloadjs';
import { useDispatch } from 'react-redux';
import { Typography, makeStyles } from '@material-ui/core';
import { useState } from 'react';
import HorizontalNavigation from '../horizontalNavigation';
import UserMenu from '../userMenu';
import { setAuthInfo } from '../../../store/modules/auth';
import { handleInstructionGuideDrawer } from '../../../store/modules/instructionGuide';
import {
  setOrganizationSettings,
  clearAllPresets,
} from '../../../store/modules/organizationSettings';
import StorageService from '../../../utility/storageService';
import http, { getHeaders } from '../../../utility/http';
import { showErrorMessage, showSuccessMessage } from '../../../utility/uiUtils';
import { getUserMenuItems } from './config';
import { sticky } from '../../../constants/helperCssRules';
import {
  API_2FA_DEACTIVATE,
  API_2FA_INITIATE,
  API_2FA_VERIFY,
  API_CUSTOMER_PORTAL,
  API_DEMO_DATA,
} from '../../../constants/apiRoutes';
import { isArrayEmpty } from '../../../utility/helpers';
import QrCodeDialog from '../qrCodeDialog';
import { isEmpty } from '../../../utility/validation';

const useStyles = makeStyles(
  ({ palette: { primary }, spacing, breakpoints }) => ({
    main: {
      borderBottom: `1px solid ${primary.bluish8}`,
      ...sticky(primary.white),
    },
    mainPerson: {
      borderBottom: 'none',
    },
    header: {
      boxSizing: 'border-box',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'nowrap',
      padding: spacing(8, 0, 6, 0),
      width: '100%',
    },
    welcomeTitle: {
      display: 'flex',
      alignItems: 'center',
      marginRight: spacing(4),
    },
    headerEnd: {
      display: 'flex',
      alignItems: 'center',
      marginLeft: 'auto',
    },
    navItem: {
      marginRight: spacing(3),
      [breakpoints.up('xLg')]: {
        marginRight: spacing(6),
      },
    },
  })
);

const PageHeader = ({
  customNavigationClass = undefined,
  customScrollArrowClass = undefined,
  headerWrapperClass = null,
  translations = {},
  navigate,
  location,
  auth,
  accessControl,
  notifications = null,
  title = '',
  navItems = [],
  renderTitle = () => {},
  organizationSettings,
  onGoToProfile,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [isAuthenticationDialogOpened, setIsAuthenticationDialogOpened] =
    useState(false);
  const [qrSecret, setQrSecret] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [hasRecoveryCodes, setHasRecoveryCodes] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState([]);

  const is2FARequired = organizationSettings?.is_2fa_required;
  const has2FA = Boolean(auth.has_2fa_enabled);

  const hasNavigation = !isArrayEmpty(navItems);

  const onManageSubscription = () => {
    http
      .post(API_CUSTOMER_PORTAL, {
        return_url: `${window.location.origin}${window.location.pathname}`,
      })
      .then(({ data }) => {
        if (data.customer_portal_url) {
          window.location.href = data.customer_portal_url;
        } else if (data.stripe_customer) {
          showErrorMessage(data.stripe_customer);
        } else {
          showErrorMessage('Something goes wrong.');
        }
      });
  };

  const onLogout = () => {
    StorageService.clear();
    http.defaults.headers = getHeaders(StorageService);
    dispatch(setAuthInfo({}));
    dispatch(clearAllPresets());
    navigate('/login');
  };

  const onAuthentication = () => {
    http.defaults.headers = getHeaders(StorageService);
    if (!has2FA) {
      http.post(API_2FA_INITIATE).then(({ data }) => {
        setQrSecret(data.secret);
        setIsAuthenticationDialogOpened(true);
      });
    } else {
      setIsAuthenticationDialogOpened(true);
    }
  };

  const handleCloseAuthenticationDialog = () => {
    setQrSecret('');
    setHasRecoveryCodes(false);
    setIsAuthenticationDialogOpened(false);
  };

  const handleConfirmAuthentication = async otpCode => {
    http.defaults.headers = getHeaders(StorageService);

    if (hasRecoveryCodes) {
      setHasRecoveryCodes(false);
      handleCloseAuthenticationDialog();
    } else {
      try {
        if (!has2FA) {
          await http
            .post(API_2FA_VERIFY, { code: otpCode })
            .then(({ data }) => {
              dispatch(
                setAuthInfo({ ...auth, has_2fa_enabled: data.has_2fa_enabled })
              );
              setRecoveryCodes(data.codes);
              setHasRecoveryCodes(true);
              setErrorMessage('');
            });
        } else {
          await http
            .post(API_2FA_DEACTIVATE, { code: otpCode })
            .then(({ data }) => {
              setIsAuthenticationDialogOpened(false);
              dispatch(
                setAuthInfo({ ...auth, has_2fa_enabled: data.has_2fa_enabled })
              );
            });
        }
      } catch ({ response }) {
        const status = response?.status;

        if (status === 429) {
          setErrorMessage(translations.qrCodeDialog.rateLimit);
        } else {
          setErrorMessage(translations.qrCodeDialog.invalid);
        }
      }
    }
  };

  const handleDownload = async () => {
    const content = recoveryCodes?.join('\n');
    download(
      new Blob([content], {
        type: 'text/plain',
      }),
      `kadar-recovery-codes.txt`
    );
  };

  const onInstructionGuideDrawerClick = (value = true) => {
    dispatch(handleInstructionGuideDrawer(value));
  };

  const onRedirect = url => () => navigate(url);

  const onClearDemoData = () => {
    http.delete(API_DEMO_DATA).then(({ data }) => {
      showSuccessMessage(translations.successMessages.demoData);
      dispatch(setOrganizationSettings({ has_demo_data: data?.has_demo_data }));
      navigate('/', {
        state: {
          shouldRefresh: true,
        },
      });
    });
  };

  const userMenuItems = getUserMenuItems(
    translations.userMenu,
    location.pathname,
    accessControl,
    organizationSettings,
    has2FA,
    is2FARequired,
    onRedirect,
    onLogout,
    onManageSubscription,
    onInstructionGuideDrawerClick,
    onClearDemoData,
    onAuthentication
  );

  return (
    <div
      className={classNames(
        classes.main,
        {
          [classes.mainPerson]: hasNavigation,
        },
        headerWrapperClass
      )}
    >
      <div className={classes.header}>
        {title ? (
          <Typography variant="h1" className={classes.welcomeTitle}>
            <span>{title}</span>
          </Typography>
        ) : (
          renderTitle()
        )}
        <div className={classes.headerEnd}>
          {notifications}
          <UserMenu
            translations={translations}
            user={auth}
            accessControl={accessControl}
            menuItems={userMenuItems}
            onGoToProfile={onGoToProfile}
          />
        </div>
      </div>
      {hasNavigation && (
        <HorizontalNavigation
          customNavItemClass={classes.navItem}
          navItems={navItems}
          className={customNavigationClass}
          scrollArrowClass={customScrollArrowClass}
        />
      )}
      <QrCodeDialog
        translations={translations.qrCodeDialog}
        isOpened={isAuthenticationDialogOpened}
        is2FARequired={is2FARequired}
        has2FA={has2FA}
        hasError={isEmpty(errorMessage)}
        errorMessage={errorMessage}
        hasRecoveryCodes={hasRecoveryCodes}
        handleDownload={handleDownload}
        onClose={handleCloseAuthenticationDialog}
        onConfirm={handleConfirmAuthentication}
        label={auth.email}
        secret={qrSecret}
      />
    </div>
  );
};

PageHeader.propTypes = {
  headerWrapperClass: PropTypes.string,
  translations: PropTypes.object,
  navigate: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  organizationSettings: PropTypes.object.isRequired,
  navItems: PropTypes.arrayOf(PropTypes.shape({})),
  auth: PropTypes.object.isRequired,
  accessControl: PropTypes.object.isRequired,
  title: PropTypes.string,
  renderTitle: PropTypes.func,
  onGoToProfile: PropTypes.func.isRequired,
  customNavigationClass: PropTypes.string,
  customScrollArrowClass: PropTypes.string,
  notifications: PropTypes.node,
};

export default PageHeader;
